import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AlertCircleIcon, LockIcon, SendIcon } from 'lucide-react';
import { WizardLayout } from '../../components/wizard/WizardLayout';
import { StepContent } from '../../components/wizard/StepContent';
import { usePortal } from '../../contexts/PortalContext';
import {
  WIZARD_STEPS,
  getStep,
  stepIndex,
  stepPath,
  type WizardStep
} from '../../data/wizardSteps';
import { validateStep, type StepErrors } from '../../utils/validation';
import type { ReadinessIssue } from '../../api/types';

export function WizardPage() {
  const { stepId } = useParams();
  const navigate = useNavigate();
  const {
    config,
    data,
    status,
    markComplete,
    save,
    saveError,
    submit,
    checkReadiness,
    ensureDraft
  } = usePortal();

  const [errors, setErrors] = useState<StepErrors>({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [serverIssues, setServerIssues] = useState<ReadinessIssue[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [documentProblems, setDocumentProblems] = useState<string[]>([]);

  const step = getStep(stepId);
  const locked = Boolean(status && !status.canEdit);

  /* The draft is created the first time a researcher opens the wizard, not at
     sign-in — committee and reviewer accounts must never get one. */
  useEffect(() => {
    void ensureDraft();
  }, [ensureDraft]);

  const validationContext = useMemo(
    () => ({
      documentSlots: config?.documentSlots ?? [],
      maxDurationMonths: config?.maxDurationMonths ?? 24
    }),
    [config]
  );

  /* The declaration step submits, as ResConsentForm.aspx did. */
  const isSubmitStep = step?.kind === 'declaration';

  /* Every earlier section that still fails its checks, shown before the
     server is consulted. */
  const localIssues = useMemo(() => {
    if (!isSubmitStep) return [];
    return WIZARD_STEPS.filter((s) => s.kind !== 'declaration')
      .map((s) => ({
        step: s,
        messages: Object.values(validateStep(s, data, validationContext))
      }))
      .filter((entry) => entry.messages.length > 0);
  }, [isSubmitStep, data, validationContext]);

  /* On reaching the declaration, ask the API what it would refuse. */
  const refreshReadiness = useCallback(async () => {
    const result = await checkReadiness();
    setServerIssues(result.issues);
  }, [checkReadiness]);

  useEffect(() => {
    if (isSubmitStep && status?.canEdit) void refreshReadiness();
  }, [refreshReadiness, status?.canEdit, isSubmitStep]);

  if (!step) return <Navigate to={stepPath(WIZARD_STEPS[0].id)} replace />;

  const index = stepIndex(step.id);
  const previousStep: WizardStep | undefined = WIZARD_STEPS[index - 1];
  const nextStep: WizardStep | undefined = WIZARD_STEPS[index + 1];

  const goTo = (target: WizardStep) => {
    setErrors({});
    navigate(stepPath(target.id));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = async () => {
    const found = validateStep(step, data, validationContext);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!locked) await save();
    markComplete(step.id);
    if (nextStep) goTo(nextStep);
  };

  const handleSubmit = async () => {
    setAttemptedSubmit(true);
    setSubmitError(null);
    setDocumentProblems([]);

    const declaration = validateStep(step, data, validationContext);
    setErrors(declaration);

    if (localIssues.length > 0 || Object.keys(declaration).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    const result = await submit();
    setSubmitting(false);

    if (result.ok) {
      navigate('/dashboard');
      return;
    }

    setSubmitError(result.message);
    setDocumentProblems(result.documentProblems);
    await refreshReadiness();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const errorCount = Object.keys(errors).length;

  /* Anything the server flagged that the local rules did not catch. */
  const extraServerIssues = serverIssues.filter(
    (issue) => !localIssues.some((local) => local.step.id === issue.StepId)
  );

  return (
    <WizardLayout
      step={step}
      previous={
        previousStep
          ? {
              label: 'Previous Step',
              subtitle: previousStep.label,
              onClick: () => goTo(previousStep)
            }
          : undefined
      }
      next={
        isSubmitStep || !nextStep
          ? undefined
          : {
              label: 'Save & Continue',
              subtitle: `Next — ${nextStep.label}`,
              onClick: () => void handleNext()
            }
      }
    >
      {locked ? (
        <div className="notice" role="status">
          <LockIcon size={18} aria-hidden="true" />
          <span>
            {status?.statusForResearcher ||
              'This proposal is with a committee, so it is read-only for now.'}{' '}
            You will be emailed if anything is sent back for correction.
          </span>
        </div>
      ) : null}

      {saveError ? (
        <div className="notice notice--error" role="alert">
          <AlertCircleIcon size={18} aria-hidden="true" />
          <span>{saveError}</span>
        </div>
      ) : null}

      {!isSubmitStep && errorCount > 0 ? (
        <div className="notice notice--error" role="alert">
          <AlertCircleIcon size={18} aria-hidden="true" />
          <span>
            {errorCount} field{errorCount > 1 ? 's need' : ' needs'} attention.
          </span>
        </div>
      ) : null}

      {isSubmitStep && submitError ? (
        <div className="notice notice--error" role="alert">
          <AlertCircleIcon size={18} aria-hidden="true" />
          {documentProblems.length > 0 ? (
            <div>
              <strong>
                Nothing has been submitted yet — {documentProblems.length} uploaded
                document{documentProblems.length > 1 ? 's' : ''} could not be combined with
                your proposal.
              </strong>
              <ul className="issue-list">
                {documentProblems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
              <button
                type="button"
                className="link-button"
                onClick={() => navigate(stepPath('documents'))}
              >
                Go to Associated Documents
              </button>
            </div>
          ) : (
            <span>{submitError}</span>
          )}
        </div>
      ) : null}

      {isSubmitStep && attemptedSubmit && localIssues.length > 0 ? (
        <div className="notice notice--error" role="alert">
          <AlertCircleIcon size={18} aria-hidden="true" />
          <div>
            <strong>
              {localIssues.length} section{localIssues.length > 1 ? 's' : ''} still need
              attention before you can submit.
            </strong>
            <ul className="issue-list">
              {localIssues.map((issue) => (
                <li key={issue.step.id}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate(stepPath(issue.step.id))}
                  >
                    {issue.step.label}
                  </button>{' '}
                  — {issue.messages[0]}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {isSubmitStep && extraServerIssues.length > 0 ? (
        <div className="notice notice--error" role="alert">
          <AlertCircleIcon size={18} aria-hidden="true" />
          <div>
            <strong>Still missing before you can submit:</strong>
            <ul className="issue-list">
              {extraServerIssues.map((issue) => (
                <li key={`${issue.StepId}-${issue.Issue}`}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate(stepPath(issue.StepId))}
                  >
                    {getStep(issue.StepId)?.label ?? issue.StepId}
                  </button>{' '}
                  — {issue.Issue}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <StepContent step={step} errors={errors} />

      {isSubmitStep ? (
        <section className="card">
          <div className="card-body submit-bar">
            <div>
              <h2 className="card-title">
                {locked ? 'Already with the committee' : 'Submit Research'}
              </h2>
              <p className="card-subtitle">
                {locked
                  ? status?.statusForResearcher ||
                    'Your proposal is under review. You will be notified of any change.'
                  : 'Once submitted, an IRB number is generated and the proposal can no longer be edited unless it is sent back for correction.'}
              </p>
            </div>
            <button
              type="button"
              className="btn btn--primary"
              disabled={locked || submitting}
              onClick={() => void handleSubmit()}
            >
              <SendIcon size={16} aria-hidden="true" />
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </section>
      ) : null}
    </WizardLayout>
  );
}
